import { RouteData } from '@cya-routes/shared'
import { getHighwayColor } from '../../utils/highwayColorUtils'

export class RouteRenderer {
  private map: any
  private renderedRoutes = new Set<string>()
  private selectedRouteId: string | null = null
  private highlightedSegment: { routeId: string, startDistance: number, endDistance: number } | null = null
  private highlightedClimbs: Array<{ routeId: string, climb: any, index: number }> = []

  constructor(map: any) {
    this.map = map
  }

  public renderRoutes(
    routes: RouteData[],
    selectedRouteId?: string,
    masterRoute?: RouteData,
    showHighwayOverlay = false
  ) {
    this.selectedRouteId = selectedRouteId || null

    if (!showHighwayOverlay) {
      for (const renderedId of this.renderedRoutes) {
        this.removeHighwayOverlay(renderedId)
      }
    }

    // Remove old routes that are no longer in the list
    const currentRouteIds = new Set(routes.map(r => r.id))
    if (masterRoute) {
      currentRouteIds.add('master-route')
    }
    
    for (const renderedId of this.renderedRoutes) {
      if (!currentRouteIds.has(renderedId)) {
        this.removeRoute(renderedId)
      }
    }

    // Remove distance markers from all routes first (to handle route selection changes)
    for (const routeId of this.renderedRoutes) {
      this.removeDistanceMarkers(routeId)
      this.removeDirectionalArrows(routeId)
    }
    
    // Also explicitly remove master route markers if switching away from master route
    if (masterRoute && selectedRouteId !== 'master-route') {
      this.removeDistanceMarkers('master-route')
      this.removeDirectionalArrows('master-route')
    }

    // IMPORTANT: Render master route FIRST (if provided) so it appears at the back
    if (masterRoute) {
      this.renderRoute(masterRoute, showHighwayOverlay)
    }

    // Then render individual routes on top
    routes.forEach(route => {
      this.renderRoute(route, showHighwayOverlay)
    })
  }

  private renderRoute(route: RouteData, showHighwayOverlay: boolean) {
    if (!route.coordinates || route.coordinates.length === 0) return

    const routeId = route.id
    this.renderedRoutes.add(routeId)

    // Create route line
    this.renderRouteLine(route)
    
    // Create highway overlays if enabled
    this.renderHighwayOverlays(route, showHighwayOverlay)

    // Create distance markers only for the selected route
    const shouldShowMarkers = this.selectedRouteId === routeId
    if (shouldShowMarkers) {
      this.renderDistanceMarkers(route)
      this.renderDirectionalArrows(route)
    } else {
      // Remove markers for unselected routes
      this.removeDistanceMarkers(routeId)
      this.removeDirectionalArrows(routeId)
    }

    // Create unpaved overlays if there are unpaved sections
    this.renderUnpavedOverlays(route)
  }

  private renderRouteLine(route: RouteData) {
    const sourceId = `route-${route.id}`
    const layerId = `route-line-${route.id}`
    
    // Check if this route has individual segments (like master route)
    if (route.id === 'master-route' && route.segments && route.segments.length > 0) {
      // Render as MultiLineString to show disconnected segments
      const validSegments = route.segments.filter(segment => 
        Array.isArray(segment) && segment.length >= 2
      )
      
      if (validSegments.length === 0) {
        console.warn(`No valid segments for route ${route.id}`)
        return
      }
      
      const geojson: GeoJSON.Feature<GeoJSON.MultiLineString> = {
        type: 'Feature',
        properties: { routeId: route.id },
        geometry: {
          type: 'MultiLineString',
          coordinates: validSegments
        }
      }
      
      // Add or update source
      if (this.map.getSource(sourceId)) {
        (this.map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geojson)
      } else {
        this.map.addSource(sourceId, {
          type: 'geojson',
          data: geojson
        })
      }
    } else {
      // Regular single route rendering
      // Validate coordinates
      if (!route.coordinates || !Array.isArray(route.coordinates) || route.coordinates.length < 2) {
        console.warn(`Invalid coordinates for route ${route.id}`)
        return
      }

      // Create GeoJSON for the route
      const geojson: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: { routeId: route.id },
        geometry: {
          type: 'LineString',
          coordinates: route.coordinates
        }
      }

      // Add or update source
      if (this.map.getSource(sourceId)) {
        (this.map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geojson)
      } else {
        this.map.addSource(sourceId, {
          type: 'geojson',
          data: geojson
        })
      }
    }

    // Add layer if it doesn't exist, or update color if it does
    if (!this.map.getLayer(layerId)) {
      // For individual routes, ensure they're added above the master route if it exists
      let beforeLayerId: string | undefined = undefined
      if (route.id !== 'master-route') {
        // Find the first layer that's not the master route to insert before
        const allLayers = this.map.getStyle().layers
        for (const layer of allLayers) {
          if (layer.id && !layer.id.includes('master-route') && layer.id.includes('route-line-')) {
            beforeLayerId = layer.id
            break
          }
        }
      }
      
      this.map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': route.color,
          'line-width': 4,
          'line-opacity': 0.8
        }
      }, beforeLayerId)

      // Add white border
      this.map.addLayer({
        id: `${layerId}-border`,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 6,
          'line-opacity': 0.6
        }
      }, layerId) // Insert below main line
    } else {
      // Update the line color if layer already exists
      this.map.setPaintProperty(layerId, 'line-color', route.color)
    }
  }

  private renderDistanceMarkers(route: RouteData) {
    if (!route.coordinates || route.coordinates.length < 2) return

    const markersSourceId = `distance-markers-${route.id}`
    
    // Calculate distance markers for all zoom levels
    const allMarkers: GeoJSON.Feature<GeoJSON.Point>[] = []
    const intervals = [1, 5, 10, 25, 50] // km intervals
    
    intervals.forEach(interval => {
      const markers = this.createDistanceMarkersForInterval(route, interval)
      allMarkers.push(...markers)
    })

    const geojson: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features: allMarkers
    }

    // Add or update source
    if (this.map.getSource(markersSourceId)) {
      (this.map.getSource(markersSourceId) as mapboxgl.GeoJSONSource).setData(geojson)
    } else {
      this.map.addSource(markersSourceId, {
        type: 'geojson',
        data: geojson
      })

      // Add markers layer with zoom-based filtering
      this.map.addLayer({
        id: `distance-markers-${route.id}`,
        type: 'circle',
        source: markersSourceId,
        filter: [
          'any',
          ['all', ['>=', ['zoom'], 13], ['==', ['get', 'interval'], 1]],
          ['all', ['>=', ['zoom'], 11], ['<', ['zoom'], 13], ['==', ['get', 'interval'], 5]],
          ['all', ['>=', ['zoom'], 9], ['<', ['zoom'], 11], ['==', ['get', 'interval'], 10]],
          ['all', ['>=', ['zoom'], 7], ['<', ['zoom'], 9], ['==', ['get', 'interval'], 25]],
          ['all', ['<', ['zoom'], 7], ['==', ['get', 'interval'], 50]]
        ],
        paint: {
          'circle-radius': 2,
          'circle-color': '#1f2937',
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff'
        }
      })

      // Add labels
      this.map.addLayer({
        id: `distance-labels-${route.id}`,
        type: 'symbol',
        source: markersSourceId,
        filter: [
          'any',
          ['all', ['>=', ['zoom'], 13], ['==', ['get', 'interval'], 1]],
          ['all', ['>=', ['zoom'], 11], ['<', ['zoom'], 13], ['==', ['get', 'interval'], 5]],
          ['all', ['>=', ['zoom'], 9], ['<', ['zoom'], 11], ['==', ['get', 'interval'], 10]],
          ['all', ['>=', ['zoom'], 7], ['<', ['zoom'], 9], ['==', ['get', 'interval'], 25]],
          ['all', ['<', ['zoom'], 7], ['==', ['get', 'interval'], 50]]
        ],
        layout: {
          'text-field': ['get', 'distance'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-offset': [0, -0.8]
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#1f2937',
          'text-halo-width': 2
        }
      })
    }
  }

  private createDistanceMarkersForInterval(route: RouteData, interval: number): GeoJSON.Feature<GeoJSON.Point>[] {
    const markers: GeoJSON.Feature<GeoJSON.Point>[] = []
    
    if (!route.coordinates || route.coordinates.length < 2) {
      return markers
    }
    
    let totalDistance = 0
    let nextMarkerDistance = interval

    for (let i = 1; i < route.coordinates.length; i++) {
      const coord1 = route.coordinates[i - 1]
      const coord2 = route.coordinates[i]
      
      if (!coord1 || !coord2 || !Array.isArray(coord1) || !Array.isArray(coord2)) {
        continue
      }
      
      const [lon1, lat1] = coord1
      const [lon2, lat2] = coord2
      
      const segmentDistance = this.calculateDistance(lat1, lon1, lat2, lon2)
      totalDistance += segmentDistance

      if (totalDistance >= nextMarkerDistance) {
        markers.push({
          type: 'Feature',
          properties: {
            distance: nextMarkerDistance.toString(),
            interval: interval
          },
          geometry: {
            type: 'Point',
            coordinates: [lon2, lat2]
          }
        })
        nextMarkerDistance += interval
      }
    }

    return markers
  }

  private renderDirectionalArrows(route: RouteData) {
    if (!route.coordinates || route.coordinates.length < 2) return

    const arrowSourceId = `directional-arrows-${route.id}`
    
    // Create arrow markers for all zoom levels
    const allArrows = this.createDirectionalArrows(route)

    const geojson: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features: allArrows
    }

    // Add or update source
    if (this.map.getSource(arrowSourceId)) {
      (this.map.getSource(arrowSourceId) as mapboxgl.GeoJSONSource).setData(geojson)
    } else {
      this.map.addSource(arrowSourceId, {
        type: 'geojson',
        data: geojson
      })

      // Add arrows layer with zoom-based filtering
      this.map.addLayer({
        id: `directional-arrows-${route.id}`,
        type: 'symbol',
        source: arrowSourceId,
        filter: [
          'any',
          ['all', ['>=', ['zoom'], 13], ['==', ['get', 'interval'], 1]],
          ['all', ['>=', ['zoom'], 11], ['<', ['zoom'], 13], ['==', ['get', 'interval'], 5]],
          ['all', ['>=', ['zoom'], 9], ['<', ['zoom'], 11], ['==', ['get', 'interval'], 10]],
          ['all', ['>=', ['zoom'], 7], ['<', ['zoom'], 9], ['==', ['get', 'interval'], 25]],
          ['all', ['<', ['zoom'], 7], ['==', ['get', 'interval'], 50]]
        ],
        layout: {
          'text-field': '→',
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': 22,
          'text-rotate': ['get', 'bearing'],
          'text-rotation-alignment': 'map',
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'text-color': '#ffffff',
          'text-opacity': 1.0,
          'text-halo-color': '#000000',
          'text-halo-width': 1
        }
      })
    }
  }

  private createDirectionalArrows(route: RouteData): GeoJSON.Feature<GeoJSON.Point>[] {
    const arrows: GeoJSON.Feature<GeoJSON.Point>[] = []
    const intervals = [1, 5, 10, 25, 50] // km intervals matching distance markers
    
    intervals.forEach(interval => {
      const intervalArrows = this.createArrowsForInterval(route, interval)
      arrows.push(...intervalArrows)
    })

    return arrows
  }

  private createArrowsForInterval(route: RouteData, interval: number): GeoJSON.Feature<GeoJSON.Point>[] {
    const arrows: GeoJSON.Feature<GeoJSON.Point>[] = []
    
    if (!route.coordinates || route.coordinates.length < 2) {
      return arrows
    }
    
    const coordinates = route.coordinates
    let totalDistance = 0
    let nextArrowDistance = interval * 0.5 // Start at half the interval to offset from distance markers

    for (let i = 1; i < coordinates.length; i++) {
      const coord1 = coordinates[i - 1]
      const coord2 = coordinates[i]
      
      if (!coord1 || !coord2 || !Array.isArray(coord1) || !Array.isArray(coord2)) {
        continue
      }
      
      const [lon1, lat1] = coord1
      const [lon2, lat2] = coord2
      
      const segmentDistance = this.calculateDistance(lat1, lon1, lat2, lon2)
      totalDistance += segmentDistance

      if (totalDistance >= nextArrowDistance) {
        // Calculate bearing (direction) for the arrow
        const bearing = this.calculateBearing(lat1, lon1, lat2, lon2)
        
        arrows.push({
          type: 'Feature',
          properties: {
            bearing: bearing,
            distance: nextArrowDistance,
            interval: interval
          },
          geometry: {
            type: 'Point',
            coordinates: [lon2, lat2]
          }
        })
        
        nextArrowDistance += interval
      }
    }

    return arrows
  }

  private calculateBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const dLon = (lon2 - lon1) * Math.PI / 180
    const lat1Rad = lat1 * Math.PI / 180
    const lat2Rad = lat2 * Math.PI / 180
    
    const y = Math.sin(dLon) * Math.cos(lat2Rad)
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - 
              Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon)
    
    const bearing = Math.atan2(y, x) * 180 / Math.PI
    // Subtract 90 degrees to correct the arrow orientation (→ symbol points right by default)
    return (bearing - 90 + 360) % 360
  }

  private renderUnpavedOverlays(route: RouteData) {
    if (!route.unpavedSegments || route.unpavedSegments.length === 0) return

    const sourceId = `unpaved-segments-${route.id}`
    const layerId = `unpaved-overlay-${route.id}`

    // Create GeoJSON for unpaved segments
    const features: GeoJSON.Feature<GeoJSON.LineString>[] = route.unpavedSegments.map((segment, index) => ({
      type: 'Feature',
      properties: { 
        routeId: route.id,
        segmentIndex: index
      },
      geometry: {
        type: 'LineString',
        coordinates: segment
      }
    }))

    const geojson: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
      type: 'FeatureCollection',
      features
    }

    // Add or update source
    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as any).setData(geojson)
    } else {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: geojson
      })
    }

    // Add unpaved overlay layer if it doesn't exist
    if (!this.map.getLayer(layerId)) {
      this.map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 2,
          'line-opacity': 0.9,
          'line-dasharray': [0.5, 1.5] // White dotted overlay for unpaved sections
        }
      })
    } else {
      // Ensure the unpaved overlay remains above other line layers (like highway overlay)
      try {
        this.map.moveLayer(layerId)
      } catch (error) {
        console.warn(`Unable to move layer ${layerId}:`, error)
      }
    }
  }

  private renderHighwayOverlays(route: RouteData, showHighwayOverlay: boolean) {
    const sourceId = `highway-segments-${route.id}`
    const layerId = `highway-overlay-${route.id}`

    if (!showHighwayOverlay) {
      this.removeHighwayOverlay(route.id)
      return
    }

    const segments = route.highwaySegments?.filter(
      segment => Array.isArray(segment.coordinates) && segment.coordinates.length >= 2
    )

    if (!segments || segments.length === 0) {
      this.removeHighwayOverlay(route.id)
      return
    }

    const features: GeoJSON.Feature<GeoJSON.LineString>[] = segments.map((segment, index) => ({
      type: 'Feature',
      properties: {
        routeId: route.id,
        segmentIndex: index,
        highway_type: segment.type || 'unknown',
        color: getHighwayColor(segment.type)
      },
      geometry: {
        type: 'LineString',
        coordinates: segment.coordinates
      }
    }))

    const geojson: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
      type: 'FeatureCollection',
      features
    }

    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geojson)
    } else {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: geojson
      })
    }

    const lineWidthExpression: any = [
      'case',
      ['==', ['get', 'routeId'], this.selectedRouteId || ''],
      5,
      3.2
    ]

    const opacityExpression: any = [
      'case',
      ['==', ['get', 'routeId'], this.selectedRouteId || ''],
      0.95,
      0.78
    ]

    if (!this.map.getLayer(layerId)) {
      this.map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': lineWidthExpression,
          'line-opacity': opacityExpression
        }
      })
    } else {
      this.map.setPaintProperty(layerId, 'line-width', lineWidthExpression)
      this.map.setPaintProperty(layerId, 'line-opacity', opacityExpression)
    }
  }

  private removeHighwayOverlay(routeId: string) {
    const layerId = `highway-overlay-${routeId}`
    const sourceId = `highway-segments-${routeId}`

    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId)
    }

    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId)
    }
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371 // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lon2 - lon1) * Math.PI / 180
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return R * c
  }

  private removeRoute(routeId: string) {
    const layersToRemove = [
      `route-line-${routeId}`,
      `route-line-${routeId}-border`,
      `distance-markers-${routeId}`,
      `distance-labels-${routeId}`,
      `directional-arrows-${routeId}`,
      `unpaved-overlay-${routeId}`,
      `highway-overlay-${routeId}`
    ]

    const sourcesToRemove = [
      `route-${routeId}`,
      `distance-markers-${routeId}`,
      `directional-arrows-${routeId}`,
      `unpaved-segments-${routeId}`,
      `highway-segments-${routeId}`
    ]

    layersToRemove.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId)
      }
    })

    sourcesToRemove.forEach(sourceId => {
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId)
      }
    })

    this.renderedRoutes.delete(routeId)
  }

  private removeDistanceMarkers(routeId: string) {
    const markersLayerId = `distance-markers-${routeId}`
    const labelsLayerId = `distance-labels-${routeId}`
    const markersSourceId = `distance-markers-${routeId}`

    if (this.map.getLayer(markersLayerId)) {
      this.map.removeLayer(markersLayerId)
    }
    if (this.map.getLayer(labelsLayerId)) {
      this.map.removeLayer(labelsLayerId)
    }
    if (this.map.getSource(markersSourceId)) {
      this.map.removeSource(markersSourceId)
    }
  }

  private removeDirectionalArrows(routeId: string) {
    const arrowsLayerId = `directional-arrows-${routeId}`
    const arrowsSourceId = `directional-arrows-${routeId}`

    if (this.map.getLayer(arrowsLayerId)) {
      this.map.removeLayer(arrowsLayerId)
    }
    if (this.map.getSource(arrowsSourceId)) {
      this.map.removeSource(arrowsSourceId)
    }
  }

  public highlightRouteSegment(routeId: string | null, startDistance: number | null, endDistance: number | null, route: RouteData | null, color?: string) {
    // Remove existing highlight
    if (this.highlightedSegment) {
      const highlightLayerId = `route-highlight-${this.highlightedSegment.routeId}`
      const highlightSourceId = `route-highlight-source-${this.highlightedSegment.routeId}`
      
      if (this.map.getLayer(highlightLayerId)) {
        this.map.removeLayer(highlightLayerId)
      }
      if (this.map.getSource(highlightSourceId)) {
        this.map.removeSource(highlightSourceId)
      }
    }

    // If no segment to highlight, just clear and return
    if (!routeId || startDistance === null || endDistance === null || !route || !route.coordinates) {
      this.highlightedSegment = null
      return
    }

    // Store new highlight info
    this.highlightedSegment = { routeId, startDistance, endDistance }

    // Extract the segment of the route to highlight
    const highlightCoordinates: [number, number][] = []
    let totalDistance = 0
    let segmentStarted = false

    for (let i = 0; i < route.coordinates.length - 1; i++) {
      const coord1 = route.coordinates[i]
      const coord2 = route.coordinates[i + 1]
      
      if (!coord1 || !coord2 || !Array.isArray(coord1) || !Array.isArray(coord2)) {
        continue
      }
      
      const [lon1, lat1] = coord1
      const [lon2, lat2] = coord2
      
      const segmentDistance = this.calculateDistance(lat1, lon1, lat2, lon2)
      const previousDistance = totalDistance
      totalDistance += segmentDistance

      // Check if we need to start collecting coordinates
      if (!segmentStarted && totalDistance >= startDistance) {
        segmentStarted = true
        
        // If the segment starts within this line segment, interpolate the start point
        if (previousDistance < startDistance) {
          const ratio = (startDistance - previousDistance) / segmentDistance
          const interpolatedLon = lon1 + (lon2 - lon1) * ratio
          const interpolatedLat = lat1 + (lat2 - lat1) * ratio
          highlightCoordinates.push([interpolatedLon, interpolatedLat])
        } else {
          highlightCoordinates.push([lon1, lat1])
        }
      }

      // If we're collecting coordinates
      if (segmentStarted) {
        // Check if we need to stop
        if (totalDistance >= endDistance) {
          // If the segment ends within this line segment, interpolate the end point
          if (previousDistance < endDistance) {
            const ratio = (endDistance - previousDistance) / segmentDistance
            const interpolatedLon = lon1 + (lon2 - lon1) * ratio
            const interpolatedLat = lat1 + (lat2 - lat1) * ratio
            highlightCoordinates.push([interpolatedLon, interpolatedLat])
          }
          break
        } else {
          highlightCoordinates.push([lon2, lat2])
        }
      }
    }

    // Only render if we have at least 2 points
    if (highlightCoordinates.length < 2) return

    const highlightSourceId = `route-highlight-source-${routeId}`
    const highlightLayerId = `route-highlight-${routeId}`

    // Create GeoJSON for the highlight
    const geojson: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: highlightCoordinates
      }
    }

    // Add source
    this.map.addSource(highlightSourceId, {
      type: 'geojson',
      data: geojson
    })

    // Add highlight layer - make it wider and brighter than the main route
    this.map.addLayer({
      id: highlightLayerId,
      type: 'line',
      source: highlightSourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': color || '#fbbf24', // Use provided color or default amber
        'line-width': 8,
        'line-opacity': 0.7
      }
    })
  }

  public highlightClimbs(routeId: string, climbs: any[], route: RouteData) {
    // Clear any existing climb highlights
    this.clearClimbHighlights()
    
    console.log(`🏔️ Highlighting ${climbs.length} climbs for route ${routeId}`)
    
    // Highlight each climb
    climbs.forEach((climb, index) => {
      console.log(`\n🧗 Processing climb ${index}: ${climb.category} from ${climb.startDistance}km to ${climb.endDistance}km`)
      const climbCoordinates: [number, number][] = []
      let totalDistance = 0
      let segmentStarted = false

      for (let i = 0; i < route.coordinates.length - 1; i++) {
        const coord1 = route.coordinates[i]
        const coord2 = route.coordinates[i + 1]
        
        // Calculate distance to next point - coordinates are [lng, lat]
        const segmentDistance = this.calculateDistance(coord1[1], coord1[0], coord2[1], coord2[0])
        
        // Check if we're within the climb segment
        // Note: segmentDistance is in km, climb distances should also be in km
        if (totalDistance + segmentDistance >= climb.startDistance && totalDistance <= climb.endDistance) {
          if (!segmentStarted) {
            // Starting the segment - might need to interpolate
            if (totalDistance < climb.startDistance) {
              const ratio = (climb.startDistance - totalDistance) / segmentDistance
              const interpLng = coord1[0] + (coord2[0] - coord1[0]) * ratio
              const interpLat = coord1[1] + (coord2[1] - coord1[1]) * ratio
              climbCoordinates.push([interpLng, interpLat])
            } else {
              climbCoordinates.push(coord1)
            }
            segmentStarted = true
          }
          
          // Check if this is the end of the climb
          if (totalDistance + segmentDistance >= climb.endDistance) {
            // Ending the segment - might need to interpolate
            const ratio = (climb.endDistance - totalDistance) / segmentDistance
            const interpLng = coord1[0] + (coord2[0] - coord1[0]) * ratio
            const interpLat = coord1[1] + (coord2[1] - coord1[1]) * ratio
            climbCoordinates.push([interpLng, interpLat])
            break
          } else {
            climbCoordinates.push(coord2)
          }
        }
        
        totalDistance += segmentDistance
      }

      console.log(`🔍 Climb ${index} processing complete: ${climbCoordinates.length} coordinates collected`)

      // Add the climb highlight if we have coordinates
      if (climbCoordinates.length >= 2) {
        const climbLayerId = `climb-highlight-${routeId}-${index}`
        const climbSourceId = `climb-highlight-source-${routeId}-${index}`
        
        console.log(`📍 Adding climb ${index}: ${climb.category} (${climbCoordinates.length} points, ${climb.startDistance.toFixed(1)}km - ${climb.endDistance.toFixed(1)}km)`)
        
        // Add source
        this.map.addSource(climbSourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: climbCoordinates
            }
          }
        })

        // Add layer with climb color - place it above the route layer if it exists
        const routeLayerId = `route-${routeId}`
        const layerConfig = {
          id: climbLayerId,
          type: 'line',
          source: climbSourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': climb.color || '#fbbf24',
            'line-width': 10,
            'line-opacity': 0.6
          }
        }
        
        // Try to add above the route layer, but if it doesn't exist, just add it
        try {
          if (this.map.getLayer(routeLayerId)) {
            this.map.addLayer(layerConfig, routeLayerId)
          } else {
            this.map.addLayer(layerConfig)
          }
        } catch (error) {
          console.warn(`⚠️ Could not add climb layer above route layer, adding normally:`, error)
          this.map.addLayer(layerConfig)
        }
        
        // Store the climb highlight info with its index
        this.highlightedClimbs.push({ routeId, climb, index })
      }
    })
  }

  public clearClimbHighlights() {
    // Remove all climb highlights
    this.highlightedClimbs.forEach((highlight) => {
      const climbLayerId = `climb-highlight-${highlight.routeId}-${highlight.index}`
      const climbSourceId = `climb-highlight-source-${highlight.routeId}-${highlight.index}`
      
      if (this.map.getLayer(climbLayerId)) {
        this.map.removeLayer(climbLayerId)
      }
      if (this.map.getSource(climbSourceId)) {
        this.map.removeSource(climbSourceId)
      }
    })
    
    this.highlightedClimbs = []
  }

  public destroy() {
    // Remove any existing highlight
    if (this.highlightedSegment) {
      const highlightLayerId = `route-highlight-${this.highlightedSegment.routeId}`
      const highlightSourceId = `route-highlight-source-${this.highlightedSegment.routeId}`
      
      if (this.map.getLayer(highlightLayerId)) {
        this.map.removeLayer(highlightLayerId)
      }
      if (this.map.getSource(highlightSourceId)) {
        this.map.removeSource(highlightSourceId)
      }
    }

    // Clear climb highlights
    this.clearClimbHighlights()

    for (const routeId of this.renderedRoutes) {
      this.removeRoute(routeId)
    }
    this.renderedRoutes.clear()
  }
}
